/* eslint-disable @typescript-eslint/no-explicit-any */

import { usePurchaseComponents } from "@contexts/PurchaseComponentsContext";
import { createClient, EntryCollection, EntrySkeletonType } from "contentful";

import { useQuery, UseQueryResult } from "@tanstack/react-query";

import { contentParsersMap, ContentType } from "./parsers";

type ContentfulConfig = {
  spaceId: string;
  accessToken: string;
  environment: string;
};

export const getContentQuery = <T extends ContentType>({
  contentType,
  locale,
  partner,
  contentful,
  enabled = true,
}: {
  contentType: T;
  locale: string;
  partner: string;
  contentful: ContentfulConfig;
  enabled?: boolean;
}) => ({
  queryKey: [contentType, locale, partner, contentful],
  enabled,
  queryFn: async (): Promise<ReturnType<(typeof contentParsersMap)[T]>> =>
    createClient({
      space: contentful.spaceId,
      environment: contentful.environment,
      accessToken: contentful.accessToken,
    })
      .withoutUnresolvableLinks.getEntries({
        locale,
        content_type: contentType,
        "fields.partnerCode": partner,
        include: 3,
      })
      .then((response) => transformContentfulResponse(response))
      .then(
        (content: { items: Array<any> }) =>
          contentParsersMap[contentType](content) as ReturnType<(typeof contentParsersMap)[T]>,
      ),
});

export const useContent = <T extends ContentType>(
  contentType: T,
  { enabled }: { enabled?: boolean } = {},
): UseQueryResult<ReturnType<(typeof contentParsersMap)[T]>> => {
  const { locale, partner, contentful } = usePurchaseComponents();

  return useQuery<ReturnType<(typeof contentParsersMap)[T]>>(
    getContentQuery({ contentType, locale, partner, contentful, enabled }),
  );
};

const transformContentfulResponse = (
  response: EntryCollection<EntrySkeletonType, "WITHOUT_UNRESOLVABLE_LINKS", string>,
) => {
  const items: Array<ContentfulItem> = response.items.map((item) => item.fields ?? item) as Array<ContentfulItem>;
  return { items: items.map((item) => prepareContentfulItem(item)) };
};

const prepareContentfulItem = (item: ContentfulItem) => {
  Object.entries(item).forEach(([key, value]) => {
    if ((value as ContentfulLink).fields) {
      item[key] = prepareContentfulItem((value as ContentfulLink).fields as ContentfulItem) as ContentfulField;
    }

    if (Array.isArray(value)) {
      item[key] = value.map((arrayItem) => {
        if (arrayItem.fields) {
          return prepareContentfulItem(arrayItem.fields as ContentfulItem) as ContentfulField;
        }

        return arrayItem;
      }) as ContentfulField;
    }
  });

  return item;
};

type ContentfulItem = Record<string, ContentfulField>;

type ContentfulField =
  | string
  | ContentfulLink
  | Array<ContentfulLink>
  | Record<string, string | ContentfulLink | Array<ContentfulLink>>;

type ContentfulLink = {
  fields: Record<string, string | ContentfulLink | Array<ContentfulLink>>;
  sys: unknown;
  metadata: unknown;
};
